import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { StepInputSchema } from '../../shared/step-input.schema';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { ProcessStepAssignmentStatusSchema } from '../process-step-assignment/process-step-assignment.schema';
import { StepHelpersSchema, StepSuccessorsSchema, StepDecisionRuleSchema, WhoElseDoesThisItemSchema } from '../process-step-recording/process-step-recording.schema';
import { EntityOrSuggestionRefSchema } from '../entity-suggestion';
import { AssigneeModelSchema } from '../../shared/assignee-model.schema';
/**
 * A `ProcessDraftAssignment` is the pre-publish counterpart of an
 * `OpenDraftAssignment`: an invitation that exists in the draft only — we
 * know who will do the work and which upstream step feeds it, but nothing
 * has been recorded yet. Publishing the draft converts this into a real
 * `ProcessStepAssignment` and dispatches the invite.
 *
 * Conceptually distinct from `ProcessDraftStep`, which represents a recorded
 * (manual) step. Both share the `stableStepId` ObjectId space so graph edges
 * (`inputs[].sourceSteps[].stepId`) work across the two types uniformly.
 */ export const ProcessDraftAssignmentSchema = z.object({
    _id: ObjectIdSchema,
    draftId: ObjectIdSchema,
    processId: ObjectIdSchema,
    stableStepId: ObjectIdSchema,
    /**
   * Assignee ref carrying the union shape. Mirrors
   * `ProcessStepRecording.assignedTo` — only OrgFunction assignees can be
   * suggestion refs; User/System/ExternalContact entries are always
   * `{ type: 'entity', id }`. The wrapper stays uniform so consumers branch
   * on `.type` identically across assignee models.
   */ assignedTo: EntityOrSuggestionRefSchema.optional(),
    assignedToModel: AssigneeModelSchema.optional(),
    /**
   * Assignments must always have at least one `previous_output` input —
   * they represent a response to an upstream step's output. Start-sourced
   * assignments carry an event input linked to the start event node instead.
   * Event inputs may ride along so converting a step with start-event
   * connections to an assignment (and back) doesn't drop those edges.
   */ inputs: z.array(StepInputSchema),
    helpers: StepHelpersSchema.optional(),
    whoElseDoesThis: z.array(WhoElseDoesThisItemSchema),
    generatedName: z.string().nullable().optional(),
    generatedDescription: z.string().nullable().optional(),
    /** Creation-time swimlane hint; see `ProcessDraftStepSchema.laneKey`. */ laneKey: z.string().optional(),
    /** When true, publishing dispatches a fresh invite for the assignee. */ dirtyForInvite: z.boolean(),
    /**
   * When this assignment was materialized from an in-flight open
   * assignment, we link back so (a) the placeholder stops showing
   * independently, and (b) publishing cancels the original assignment.
   */ supersedesAssignment: ObjectIdSchema.optional(),
    /**
   * Status of the live invitation at draft-creation time. Frozen — not kept
   * in sync with live after that. Absent on legacy or draft-authored
   * assignments; consumers should treat absent as `'pending'`.
   */ statusSnapshot: ProcessStepAssignmentStatusSchema.optional(),
    /**
   * Preserves task content across mode conversions: if the user converts
   * this assignment back into a step, we restore these fields. Keeps the
   * user's in-progress writing safe across accidental mode flips.
   */ stashedStep: z.object({
        actionType: StepActionTypeSchema.optional(),
        task: z.object({
            taskDescription: z.string().optional(),
            output: z.string().optional(),
            successors: StepSuccessorsSchema.optional()
        }).optional(),
        rules: z.array(StepDecisionRuleSchema).optional()
    }).optional(),
    updatedAt: z.date()
});

//# sourceMappingURL=process-draft-assignment.schema.js.map